import React, { useState, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { 
  User, Camera, Mail, Crown, Calendar, Edit2, Save, X,
  ArrowLeft, Trash2, Check, Settings, Bell, Shield, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { updateProfile, uploadProfilePicture, deleteProfilePicture } from '../services/api';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

function ProfilePage() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    bio: user?.bio || ''
  });
  const fileInputRef = useRef(null);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      await uploadProfilePicture(file);
      await refreshUser();
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!user?.profile_picture) return;
    
    try {
      await deleteProfilePicture();
      await refreshUser();
      toast.success('Profile picture removed');
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      await refreshUser();
      setIsEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'contributor': return 'text-purple-500 bg-purple-500/10';
      case 'creator': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-[var(--text-secondary)] bg-[var(--surface)]';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'contributor': return 'Contributor';
      case 'creator': return 'Creator';
      default: return 'Basic';
    }
  };

  return (
    <div className="min-h-screen py-12 page-padding" data-testid="profile-page">
      <SEO title="Profile Settings" />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] mb-2">
              <ArrowLeft size={18} />
              Back to Dashboard
            </Link>
            <h1 className="text-4xl font-teko">Profile Settings</h1>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
          {/* Cover/Header Area */}
          <div className="h-32 bg-gradient-to-r from-[#E63946] to-[#FF6B35]" />
          
          {/* Profile Info */}
          <div className="px-6 pb-6">
            {/* Avatar */}
            <div className="relative -mt-16 mb-4">
              <div className="relative w-32 h-32 rounded-full border-4 border-[var(--surface)] overflow-hidden bg-[var(--background)]">
                {user?.profile_picture ? (
                  <img 
                    src={user.profile_picture} 
                    alt={user.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-[var(--primary)]">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                
                {/* Upload Overlay */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="upload-avatar-btn"
                >
                  {uploading ? (
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="text-white" size={28} />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              
              {/* Remove Image Button */}
              {user?.profile_picture && (
                <button
                  onClick={handleRemoveImage}
                  className="absolute bottom-0 right-0 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  title="Remove photo"
                  data-testid="remove-avatar-btn"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>

            {/* User Info */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[var(--text-secondary)] mb-1 block">Username</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)]"
                        data-testid="username-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[var(--text-secondary)] mb-1 block">Bio</label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={3}
                        placeholder="Tell us about yourself..."
                        className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
                        data-testid="bio-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                        data-testid="save-profile-btn"
                      >
                        {saving ? 'Saving...' : <><Save size={16} className="mr-2" /> Save</>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({ username: user?.username || '', bio: user?.bio || '' });
                        }}
                      >
                        <X size={16} className="mr-2" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-teko">{user?.username}</h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTierColor(user?.subscription_tier)}`}>
                        {getTierLabel(user?.subscription_tier)}
                      </span>
                    </div>
                    <p className="text-[var(--text-secondary)] mb-4">
                      {user?.bio || 'No bio yet'}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-[var(--text-secondary)]">
                      <span className="flex items-center gap-1">
                        <Mail size={14} />
                        {user?.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Joined {new Date(user?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {!isEditing && (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-profile-btn"
                >
                  <Edit2 size={16} className="mr-2" /> Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Subscription */}
          <Link 
            to="/pricing"
            className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 hover:border-[var(--primary)]/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[var(--primary)]/10">
                <Crown className="text-[var(--primary)]" size={24} />
              </div>
              <div>
                <h3 className="font-teko text-xl group-hover:text-[var(--primary)] transition-colors">Subscription</h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  {(user?.subscription_tier === 'free' || user?.subscription_tier === 'basic' || !user?.subscription_tier) ? 'Upgrade your plan' : 'Manage your subscription'}
                </p>
              </div>
            </div>
          </Link>

          {/* Payment Methods */}
          <Link 
            to="/dashboard"
            className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 hover:border-[var(--primary)]/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-500/10">
                <CreditCard className="text-green-500" size={24} />
              </div>
              <div>
                <h3 className="font-teko text-xl group-hover:text-[var(--primary)] transition-colors">Payment History</h3>
                <p className="text-sm text-[var(--text-secondary)]">View your transactions</p>
              </div>
            </div>
          </Link>

          {/* Notifications */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 opacity-60">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Bell className="text-blue-500" size={24} />
              </div>
              <div>
                <h3 className="font-teko text-xl">Notifications</h3>
                <p className="text-sm text-[var(--text-secondary)]">Coming soon</p>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 opacity-60">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Shield className="text-purple-500" size={24} />
              </div>
              <div>
                <h3 className="font-teko text-xl">Security</h3>
                <p className="text-sm text-[var(--text-secondary)]">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
